import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { COLORS, Row, Col, Separator, TextInput } from '@cuemath/comps';
import { Text, Button } from '@cuemath/web-comps';

import { closeModal } from 'actions/modal';
import {
  usePaymentFlowOverridesPatch,
  useTeacherSearchGetAll,
} from 'actions/payment-flow-overrides';

const PLAN_TIER = { BASIC: 'BASIC', PLUS: 'PLUS' };
const TEACHER_SEARCH_MIN = 4;

const Container = styled(Col)`
  padding: 24px;
  gap: 4px;
  overflow-y: auto;
`;

const Section = styled(Col)`
  padding: 14px 0;
  border-top: 1px solid ${COLORS.GERANIUM || '#eee'};
  gap: 8px;
`;

const Banner = styled(Row)`
  background: ${props => (props.$error ? '#fdecea' : '#fff6e5')};
  border: 1px solid ${props => (props.$error ? '#f5c6cb' : '#ffe0a3')};
  border-radius: 6px;
  padding: 10px 12px;
`;

const Segmented = styled(Row)`
  gap: 8px;
`;

const Seg = styled.button`
  border: 1px solid ${props => (props.$active ? COLORS.CTA_LINK || '#1a73e8' : '#ccc')};
  background: ${props => (props.$active ? 'rgba(26,115,232,0.08)' : COLORS.WHITE)};
  color: ${props =>
    props.$active ? COLORS.CTA_LINK || '#1a73e8' : COLORS.TEXT || '#222'};
  border-radius: 6px;
  padding: 8px 18px;
  font-weight: 600;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
`;

const TeacherResult = styled(Row)`
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  justify-content: space-between;
  &:hover {
    background: ${COLORS.BACKGROUND || '#f5f5f5'};
  }
`;

const ReadOnly = styled(Text)`
  color: ${COLORS.GREY_2 || '#666'};
`;

const Footer = styled(Row)`
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
`;

function PaymentFlowOverridesModal({ student, viewData, onSaved }) {
  const dispatch = useDispatch();
  const close = () => dispatch(closeModal());

  const studentId = student && student.id;
  const canEdit = !!(
    viewData &&
    viewData.permissions &&
    viewData.permissions.can_set_overrides
  );
  const current = (viewData && viewData.current_overrides) || {};
  const defaults = (viewData && viewData.effective_defaults) || {};
  const grade = parseInt(viewData && viewData.grade, 10);
  const gradeInRange = grade >= 2 && grade <= 5;
  // India's upgrade tier surfaces as IBG; international as PLUS. Either way the
  // stored value is PLAN_TIER.PLUS (the backend maps PLUS -> IBG for INR).
  const isIndia = ((viewData && viewData.currency) || '').toUpperCase() === 'INR';
  const upgradeLabel = isIndia ? 'IBG' : 'PLUS';

  // plan_tier: a simple BASIC/PLUS choice, only for grades 2-5.
  const initialTier = current.plan_tier || defaults.plan_tier || PLAN_TIER.BASIC;
  const initialUpgraded = current.plan_tier === PLAN_TIER.PLUS;
  const [tier, setTier] = useState(
    initialTier === PLAN_TIER.PLUS ? PLAN_TIER.PLUS : PLAN_TIER.BASIC,
  );

  // preferred_teacher
  const [teacherId, setTeacherId] = useState(current.preferred_teacher_id || null);
  const [teacherLabel, setTeacherLabel] = useState(
    current.preferred_teacher_id ? current.preferred_teacher_id : '',
  );
  const [search, setSearch] = useState('');
  const debounceRef = useRef(null);

  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState('');

  const {
    data: teacherData,
    getAll: searchTeachers,
    isProcessing: searching,
  } = useTeacherSearchGetAll('pfo-teacher-search');
  const teachers = Array.isArray(teacherData) ? teacherData : [];

  const { patch, isProcessing: saving } = usePaymentFlowOverridesPatch({
    onComplete: error => {
      if (error) {
        setFormError(error);
      } else {
        if (onSaved) onSaved();
        close();
      }
    },
  });

  useEffect(() => {
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, []);

  const onSearchChange = value => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < TEACHER_SEARCH_MIN) return;
    debounceRef.current = setTimeout(() => {
      searchTeachers({
        page: 1,
        per_page: 20,
        state: ['ACT', 'FDTR'],
        search: value,
        with_teacher_band: true,
      });
    }, 800);
  };

  const selectTeacher = t => {
    setTeacherId(t.id);
    setTeacherLabel(`${t.name || 'Teacher'}${t.phone ? ' - ' + t.phone : ''}`);
    setSearch('');
  };

  const buildChanges = () => {
    const body = {};
    const upgraded = tier === PLAN_TIER.PLUS;
    if (gradeInRange && upgraded !== initialUpgraded) {
      body.plan_tier = upgraded ? PLAN_TIER.PLUS : PLAN_TIER.BASIC;
    }
    if ((teacherId || null) !== (current.preferred_teacher_id || null)) {
      body.preferred_teacher_id = teacherId || null;
    }
    return body;
  };

  const onSave = () => {
    setFormError('');
    const changes = buildChanges();
    if (!Object.keys(changes).length) {
      setFormError('No changes to save.');
      return;
    }
    if (!reason.trim()) {
      setFormError('A reason is required.');
      return;
    }
    patch(studentId, { ...changes, reason: reason.trim() });
  };

  return (
    <Container>
      <Text variant="body1Bold">Edit payment setup</Text>
      <Separator height={2} transparent />
      <ReadOnly variant="caption">
        Overrides are single-use and clear once a payment confirms.
      </ReadOnly>
      <Separator height={8} transparent />

      {!canEdit && (
        <Banner>
          <Text variant="body2">
            You are not on the payment-flow-overrides allow-list — these controls are
            read-only. Ask the allow-list owner for access.
          </Text>
        </Banner>
      )}

      {/* plan_tier */}
      <Section>
        <Text variant="body2Bold">Enrollment type</Text>
        {gradeInRange ? (
          <>
            <ReadOnly variant="caption">
              Grade {grade}: upgrade the enrollment type from BASIC to {upgradeLabel}.
              Default is BASIC.
            </ReadOnly>
            <Segmented>
              <Seg
                type="button"
                $active={tier === PLAN_TIER.BASIC}
                disabled={!canEdit}
                onClick={() => canEdit && setTier(PLAN_TIER.BASIC)}
              >
                BASIC
              </Seg>
              <Seg
                type="button"
                $active={tier === PLAN_TIER.PLUS}
                disabled={!canEdit}
                onClick={() => canEdit && setTier(PLAN_TIER.PLUS)}
              >
                {upgradeLabel}
              </Seg>
            </Segmented>
          </>
        ) : (
          <ReadOnly variant="body2">
            {defaults.plan_tier || '-'} — tier is changeable only for grades 2–5.
          </ReadOnly>
        )}
      </Section>

      {/* preferred_teacher */}
      <Section>
        <Text variant="body2Bold">Preferred teacher (no-trial flow)</Text>
        {teacherId ? (
          <Row gap={8} alignItems="center">
            <Text variant="body2">{teacherLabel || teacherId}</Text>
            {canEdit && (
              <Seg
                type="button"
                onClick={() => {
                  setTeacherId(null);
                  setTeacherLabel('');
                }}
              >
                Clear
              </Seg>
            )}
          </Row>
        ) : (
          <ReadOnly variant="caption">None set</ReadOnly>
        )}
        {canEdit && (
          <>
            <TextInput
              placeholder="Search teacher by name (min 4 chars)"
              value={search}
              onChange={onSearchChange}
            />
            {search.length >= TEACHER_SEARCH_MIN && (
              <>
                {searching && <ReadOnly variant="caption">Searching…</ReadOnly>}
                {teachers.slice(0, 8).map(t => (
                  <TeacherResult key={t.id} onClick={() => selectTeacher(t)}>
                    <Text variant="body2">
                      {t.name} {t.phone ? `- ${t.phone}` : ''}
                    </Text>
                    <Text variant="caption">select</Text>
                  </TeacherResult>
                ))}
              </>
            )}
          </>
        )}
      </Section>

      {/* reason */}
      <Section>
        <Text variant="body2Bold">Reason (required)</Text>
        <TextInput
          placeholder="Why is this override being set?"
          value={reason}
          onChange={setReason}
          disabled={!canEdit}
        />
      </Section>

      {formError && (
        <Banner $error>
          <Text variant="body2">{formError}</Text>
        </Banner>
      )}

      <Footer>
        <Button variant="secondary" size="small" label="Cancel" onClick={close} />
        <Button
          size="small"
          label={saving ? 'Saving…' : 'Save'}
          onClick={onSave}
          disabled={!canEdit || saving}
        />
      </Footer>
    </Container>
  );
}

export default PaymentFlowOverridesModal;
